import { buildAskPendoArtifactSpacePayload } from '@/spaces/utils/ask-pendo-artifact-space-payloads';
import type { SpaceArtifactIntent, SpaceNodePayload } from '@/spaces/types/types';
import { useSpaces } from '@/spaces/use/use-spaces';
import { i18n_t as $t } from '@/utils/i18n';
import { PendoNotification } from '@pendo/components-vue3';
import { computed, ref, type ComputedRef } from 'vue';
import { hasBetaAccess, waitForBetaOptIns } from '@/settings/subscription-settings/betas/betas.utils';
import { BETA_BY_ID } from '@/settings/subscription-settings/betas/betas.helpers';

// betas.helpers.js is untyped JS; BETA_BY_ID infers as `{}` for TS consumers.
const spacesBeta = (BETA_BY_ID as Record<string, unknown>).spaces;

export type ArtifactMenuSubItem = {
    id: string;
    label: string;
    onClick: () => void;
};

export type ArtifactMenuItem = {
    id: string;
    icon?: string;
    label: string;
    disabled?: boolean;
    onClick?: () => void;
    items?: ArtifactMenuSubItem[];
};

/** Resolved when the user picks a space (call through `context()` so streamed props stay fresh). */
export type AskPendoAddToSpaceMenuContext = {
    artifactId?: string | null;
    name?: string;
    data?: unknown;
    /** Ask Pendo conversation this artifact came from (stored on the space item for provenance). */
    conversationId?: string | null;
    /** Thread message containing this artifact (stored for reopen + scroll). */
    messageId?: string | null;
    /** Use for `artifactType: 'guide'` — becomes the document body on the canvas. */
    documentMarkdown?: string;
};

export type AppendAddToSpaceMenuItemArgs = {
    spacesFlag: boolean;
    artifactType: string;
    context: () => AskPendoAddToSpaceMenuContext;
};

export const pendingArtifactIntent = ref<SpaceArtifactIntent | null>(null);

export const isCreateSpaceModalOpen = ref(false);

export function openCreateSpaceModalWithIntent (intent: SpaceArtifactIntent): void {
    pendingArtifactIntent.value = intent;
    isCreateSpaceModalOpen.value = true;
}

export function closeCreateSpaceModalWithIntentClear (): void {
    isCreateSpaceModalOpen.value = false;
    pendingArtifactIntent.value = null;
}

export function hasSpacesBetaAccess (): boolean {
    return hasBetaAccess(spacesBeta);
}

// Reactive counterpart for components that build a menu once and need to rebuild it if enrollment
// state loads after mount — hasSpacesBetaAccess() alone doesn't trigger a re-render.
export function useSpacesBetaAccess (): ComputedRef<boolean> {
    return computed(() => hasBetaAccess(spacesBeta));
}

export async function waitForSpacesBetaOptIns (): Promise<void> {
    await waitForBetaOptIns(spacesBeta);
}

/**
 * Composable for Ask Pendo artifact overflow menus: loads spaces and appends an "Add to Space" submenu.
 *
 * @returns `appendAddToSpaceMenuItem` — merges the submenu into your `ArtifactMenuItem[]` when the flag is on
 */
export function useAskPendoArtifactAddToSpace () {
    const { spaces, fetchSpaces, fetchingSpaces, getSpaceById, addNodeToBacklogOfSpace } = useSpaces();

    /**
     * Resolves payload from artifact type + context, then posts a backlog node unless it already exists.
     */
    async function addAskPendoArtifactToSpace (opts: {
        artifactType: string;
        artifactId: string | null;
        name: string;
        data: unknown;
        spaceId: string;
        documentMarkdown?: string;
        conversationId?: string | null;
        messageId?: string | null;
    }): Promise<void> {
        const { artifactType, artifactId, name, data, spaceId, documentMarkdown, conversationId, messageId } = opts;
        if (!artifactId) {
            return;
        }

        const spec = buildAskPendoArtifactSpacePayload(artifactType, {
            artifactId,
            name,
            data,
            documentMarkdown,
            conversationId,
            messageId,
        });
        if (!spec) {
            return;
        }

        const space = getSpaceById(spaceId);
        const alreadyExists = space?.nodes?.some((node) => (node.data as { artifactId?: string })?.artifactId === artifactId);
        if (alreadyExists) {
            PendoNotification({
                type: 'info',
                title: $t('spaces.Already added to Space'),
            });

            return;
        }

        const payload: SpaceNodePayload = {
            ...spec,
            isBacklog: true,
            isLive: false,
        };

        await addNodeToBacklogOfSpace(spaceId, payload);
        PendoNotification({
            type: 'success',
            title: $t('spaces.Added to Space'),
        });
    }

    async function createSpaceFromAskPendoArtifact (opts: {
        artifactType: string;
        artifactId: string | null;
        name: string;
        data: unknown;
        documentMarkdown?: string;
        conversationId?: string | null;
        messageId?: string | null;
    }): Promise<void> {
        const { artifactType, artifactId, name, data, documentMarkdown, conversationId, messageId } = opts;
        if (!artifactId) {
            return;
        }

        const spec = buildAskPendoArtifactSpacePayload(artifactType, {
            artifactId,
            name,
            data,
            documentMarkdown,
            conversationId,
            messageId,
        });
        if (!spec) {
            return;
        }

        openCreateSpaceModalWithIntent({
            artifact: {
                type: spec.type,
                kind: spec.kind,
                name: spec.name,
                data: spec.data as Record<string, unknown>,
            },
            placement: {
                isBacklog: true,
                isLive: false,
                width: spec.width,
                height: spec.height,
                x: 0,
                y: 0,
            },
        });
    }

    return {
        /**
         * Pushes a nested menu item listing spaces; each choice adds the artifact via the spaces API.
         *
         * @param items - Mutable artifact menu array from the SFC
         * @param args - Feature flag, artifact type, and lazy `context()` for id/name/data
         */
        async appendAddToSpaceMenuItem (
            items: ArtifactMenuItem[],
            args: AppendAddToSpaceMenuItemArgs,
        ): Promise<void> {
            if (!args.spacesFlag) {
                return;
            }

            await fetchSpaces();

            const spaceList = spaces.value.filter((s) => s.can?.add === true);
            const isFetchingSpaces = fetchingSpaces.value;
            const { artifactType, context: resolveContext } = args;

            items.push({
                id: 'add-to-space',
                icon: 'layout',
                label: $t('spaces.Add to Space'),
                disabled: isFetchingSpaces,
                items: [
                    {
                        id: 'create-space-from-artifact',
                        label: $t('spaces.Create Space'),
                        onClick: () => {
                            const ctx = resolveContext();
                            createSpaceFromAskPendoArtifact({
                                artifactType,
                                artifactId: ctx.artifactId ?? null,
                                name: String(ctx.name ?? ''),
                                data: ctx.data,
                                documentMarkdown: ctx.documentMarkdown,
                                conversationId: ctx.conversationId,
                                messageId: ctx.messageId,
                            }).catch((error: unknown) => {
                                const message = error instanceof Error
                                    ? error.message
                                    : $t('spaces.createSpaceError');
                                PendoNotification({
                                    type: 'error',
                                    title: message,
                                });
                            });
                        },
                    },
                    ...spaceList.map((space) => ({
                        id: space.id,
                        label: space.name,
                        onClick: () => {
                            const ctx = resolveContext();
                            addAskPendoArtifactToSpace({
                                artifactType,
                                artifactId: ctx.artifactId ?? null,
                                name: String(ctx.name ?? ''),
                                data: ctx.data,
                                spaceId: space.id,
                                documentMarkdown: ctx.documentMarkdown,
                                conversationId: ctx.conversationId,
                                messageId: ctx.messageId,
                            }).catch(() => {});
                        },
                    })),
                ],
            });
        },
    };
}
